<script lang="ts" setup>
    import { computed } from "vue";
    import { AlignmentTypeStyle, PaginationButtonSizeStyle, IconTypes, DisplayStyle } from "../../Enums/UiEnums";
    import Icon from '../../components/Icon.vue'

    const props = withDefaults(defineProps<{        
            itemsPerPage?: number,
            currentTopItem: number,
            totalItems: number,
            alignStyle?: AlignmentTypeStyle,
            buttonSize?: PaginationButtonSizeStyle,
            numberMaxPagesToDisplay?: number
        }>(), {
            itemsPerPage: 10,
            alignStyle: AlignmentTypeStyle.Right,
            buttonSize: PaginationButtonSizeStyle.Small,
            numberMaxPagesToDisplay: 3
        }); 

    const emit = defineEmits<{
        click: [currentTopItem: number, currentPage: number],
        "update:currentTopItem": [currentTopItem: number]
    }>();

       
    const numberOfPages = computed<number>(() => {
        return Math.ceil((props.totalItems) / props.itemsPerPage);
	});

    const pageNumber = computed<number>(() => {
		return (Math.ceil(props.currentTopItem / props.itemsPerPage) + 1);
	});

    const pageNumbers = Array.from({length: numberOfPages.value}, (_, i) => i + 1);

    const pagesToDisplay = computed<number>(() => {            
        return numberOfPages.value < props.numberMaxPagesToDisplay ? numberOfPages.value : props.numberMaxPagesToDisplay;
    });
     
    const startPage = computed<number>(() => {        
        const startPageCalc = ((pageNumber.value - 1) % pagesToDisplay.value == 0) ? (pagesToDisplay.value * (((pageNumber.value - 1)/pagesToDisplay.value)) + 1) : (pagesToDisplay.value * Math.trunc((((pageNumber.value - 1)/pagesToDisplay.value)))) + 1;
        return pageNumber.value > pagesToDisplay.value ? startPageCalc : 1;
    });

    function nextPage() {
        if (pageNumber.value === numberOfPages.value) {
            return;
        }
        setCurrentPageIndex(pageNumber.value);
    }

    function previousPage() {
        if (1 === pageNumber.value) {
            return;
        }
        setCurrentPageIndex(pageNumber.value - 2);
    }

    function setCurrentPageIndex(pageIndex: number) {
        let nextTopItem = pageIndex * props.itemsPerPage;
        if (nextTopItem > (props.totalItems - 1)) {
            nextTopItem = (props.totalItems - 1);
        } 
        if (nextTopItem < 0) {
            nextTopItem = 0;
        }       
        emit("click", nextTopItem, pageIndex);
        emit("update:currentTopItem", nextTopItem);
    }

    function goToPage(pageNumber: number) {
        setCurrentPageIndex(pageNumber-1);
    }


</script>

<style scoped>
span.RowDetail {
    display: flex;
    color:  black;
    font-weight: normal;
    align-items : center;
    font-size: 8pt;
    FONT-FAMILY: Tahoma, Arial,MS Sans Serif, Helvetica, sans-serif;
    white-space: nowrap;
}

.btn-group .btn:hover {
      background-color: lightgray !important; 
      border-color: gray !important; 
    }
</style>


<template>
    <div :class="props.alignStyle" class="btn-toolbar mb-3" role="toolbar" aria-label="Pagination button">
        <div class="btn-group me-2" :class="props.buttonSize" role="group" aria-label="Pagination button group">
            <span class="RowDetail pe-4">Record Count: {{ props.totalItems }}</span> 
            
            <button type="button" class="btn btn-outline-secondary" aria-label="Go to first page" :disabled="pageNumber === 1" @click="goToPage(1)">
                <Icon :icon="IconTypes.AngleDoubleLeft" :title="'Go to first page'" :displayStyle="DisplayStyle.Secondary"></Icon>
            </button>
            <button type="button" class="btn btn-outline-secondary" aria-label="Go to previous page" :disabled="pageNumber === 1" @click="previousPage()">
                <Icon :icon="IconTypes.ChveronLeft" :title="'Go to previous page'" :displayStyle="DisplayStyle.Secondary"></Icon>
            </button>   
            <button type="button" v-if="pagesToDisplay < numberOfPages && !pageNumbers.slice(startPage - 1, pagesToDisplay + startPage - 1).includes(1)" class="btn btn-outline-secondary" aria-label="more pages" :disabled="true">
                <Icon :icon="IconTypes.Ellipsis" :displayStyle="DisplayStyle.Secondary"></Icon>
            </button>             
            <button type="button" 
                v-for="index in pageNumbers.slice(startPage - 1, pageNumber < numberOfPages ? (pagesToDisplay + startPage - 1) : numberOfPages)" 
                class="btn btn-outline-secondary"
                :id="`button` + index.toString()"
                aria-label="Page numbers"
                :key="index"
                :class="{ 'active': pageNumber === index }"
                :title="`Page ` + index.toString()" 
                @click="setCurrentPageIndex(index-1)"
            >
                {{ index }}
            </button>

            <button type="button" v-if="pagesToDisplay < numberOfPages && !pageNumbers.slice(startPage - 1, pagesToDisplay + startPage - 1).includes(numberOfPages)" class="btn btn-outline-secondary" aria-label="more pages" :disabled="true">
                <Icon :icon="IconTypes.Ellipsis" :displayStyle="DisplayStyle.Secondary"></Icon>
            </button>
            <button type="button" class="btn btn-outline-secondary" aria-label="Go to next page" :disabled="pageNumber === numberOfPages || !numberOfPages" @click="nextPage()">
                <Icon :icon="IconTypes.ChveronRight" :title="'Go to next page'" :displayStyle="DisplayStyle.Secondary"></Icon>
            </button>
            <button type="button" class="btn btn-outline-secondary" aria-label="Go to last page" :disabled="pageNumber === numberOfPages" @click="goToPage(numberOfPages)">
                <Icon :icon="IconTypes.AngleDoubleRight" :title="'Go to last page'" :displayStyle="DisplayStyle.Secondary"></Icon>
            </button>
            <span class="RowDetail ps-2">Page {{ pageNumber }} of {{ numberOfPages }}</span>
        </div>
    </div>
</template>